// src/app/jobs/browse-jobs-taxonomy/browse-jobs-taxonomy.component.ts
import { Component, computed, inject, signal, OnInit, DestroyRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SeoService } from '@/app/core/seo/seo.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DecimalPipe } from '@angular/common';
import { ActiveFilter, BrowseJobsService, TaxonomyItem } from '../browse-jobs.service';
import { JobSearchCardComponent } from '../../job-search-card/job-search-card.component';
import { JobCountsPublicDto, JobPositionPublicDto, JobSearchResultPublicDto } from '@/app/models/public';

const LIMIT = 24;

interface SidebarSection {
  slug: string;
  label: string;
  items: TaxonomyItem[];
}

@Component({
  selector: 'app-browse-jobs-taxonomy',
  templateUrl: './browse-jobs-taxonomy.component.html',
  imports: [
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    DecimalPipe,
    JobSearchCardComponent,
  ],
})
export class BrowseJobsTaxonomyComponent implements OnInit {
  private http          = inject(HttpClient);
  private route         = inject(ActivatedRoute);
  private seo           = inject(SeoService);
  private browseService = inject(BrowseJobsService);
  private destroyRef    = inject(DestroyRef);

  loading      = signal(true);
  notFound     = signal(false);
  jobs         = signal<JobPositionPublicDto[]>([]);
  totalJobs    = signal(0);
  currentPage  = signal(1);
  seed         = signal<number | null>(null);
  activeFilters = signal<ActiveFilter[]>([]);
  sidebarSections = signal<SidebarSection[]>([]);

  totalPages = computed(() =>
    this.totalJobs() > 0 ? Math.ceil(this.totalJobs() / LIMIT) : 1
  );

  pageContext = computed(() =>
    this.browseService.buildPageContext(this.activeFilters())
  );

  // Breadcrumb trail built from active filters
  breadcrumbs = computed(() => {
    const crumbs: { label: string; url: string | null }[] = [
      { label: 'Home',        url: '/' },
      { label: 'Browse Jobs', url: '/browse-jobs' },
    ];

    let path = '';
    for (const filter of this.activeFilters()) {
      path += `${filter.dimension}/${filter.value}/`;
      crumbs.push({ label: filter.valueLabel, url: `/browse-jobs/${path}` });
    }

    // Last crumb has no link
    if (crumbs.length > 2) {
      crumbs[crumbs.length - 1].url = null;
    }

    return crumbs;
  });

  // Active filter removal URLs — removing a filter goes back to the path without it
  removalUrls = computed(() =>
    this.activeFilters().map((_, index) => {
      const segments = this.activeFilters()
        .slice(0, index)
        .flatMap(f => [f.dimension, f.value]);
      return segments.length
        ? `/browse-jobs/${segments.join('/')}`
        : '/browse-jobs';
    })
  );

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        switchMap(params => {
          const rawPath = params.get('browsePath') ?? '';
          const segments = rawPath.split('/').filter(Boolean);

          const filters = this.browseService.parseSegments(segments);

          if (!filters) {
            this.notFound.set(true);
            this.loading.set(false);
            return of(null);
          }

          this.activeFilters.set(filters);
          this.currentPage.set(
            Number(this.route.snapshot.queryParamMap.get('paged') ?? '1')
          );
          this.seed.set(
            this.route.snapshot.queryParamMap.get('seed')
              ? Number(this.route.snapshot.queryParamMap.get('seed'))
              : null
          );

          const ctx = this.browseService.buildPageContext(filters);
          this.seo.setPage({
            title: ctx.title,
            description: ctx.description,
            path: `/browse-jobs/${segments.join('/')}`,
          });

          const apiParams = this.browseService.resolveApiParams(filters);
          const offset    = (this.currentPage() - 1) * LIMIT;

          let jobParams = new HttpParams()
            .set('limit', LIMIT)
            .set('offset', offset);

          for (const [k, v] of Object.entries(apiParams)) {
            jobParams = jobParams.set(k, v);
          }
          if (this.seed()) jobParams = jobParams.set('seed', this.seed()!);

          // Fetch jobs and counts in parallel
          return of({ apiParams, jobParams });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        if (!result) return;

        const { apiParams, jobParams } = result;

        // Jobs fetch
        this.http.get<JobSearchResultPublicDto>('/api/public/jobs', { params: jobParams }).pipe(
          catchError(() => of({ results: [], total: 0, limit: LIMIT, offset: 0, seed: null })),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(res => {
          this.jobs.set(res.results ?? []);
          this.totalJobs.set(res.total ?? 0);
          if (res.seed && !this.seed()) this.seed.set(res.seed);
          this.loading.set(false);
        });

        // Counts fetch for sidebar
        let countParams = new HttpParams();
        for (const [k, v] of Object.entries(apiParams)) {
          countParams = countParams.set(k, v);
        }

        this.http.get<JobCountsPublicDto>('/api/public/jobs/counts', { params: countParams }).pipe(
          catchError(() => of(null)),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(counts => {
          if (!counts) return;

          const lookup = this.browseService.buildCountLookup({
            byState:          counts.byState,
            byDriverType:     counts.byDriverType,
            byCdlClass:       counts.byCdlClass,
            byFreightType:    counts.byFreightType,
            byEndorsement:    counts.byEndorsement,
            byExperienceType: counts.byExperienceType,
          });

          const activeDimensions = new Set(this.activeFilters().map(f => f.dimension));
          const currentSegments  = this.activeFilters().flatMap(f => [f.dimension, f.value]);
          const sections: SidebarSection[] = [];

          for (const slug of Object.keys(this.browseService.taxonomyMaps)) {
            if (activeDimensions.has(slug)) continue;

            const sortByCount = slug !== 'state';
            const items = this.browseService.getItemsForDimension(
              slug, lookup, currentSegments, sortByCount
            );
            if (!items.length) continue;

            sections.push({
              slug,
              label: this.browseService.dimensionLabels[slug] ?? slug,
              items,
            });
          }

          this.sidebarSections.set(sections);
        });
      });
  }

  goToPage(page: number): void {
    this.currentPage.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}